import React, { useState } from 'react';
import clsx from 'clsx';
import { FormHelperText, IconButton, TextField, Button, CircularProgress, Switch } from '@mui/material';
import { Edit, Settings } from '@mui/icons-material';
import { WorkflowOutput as Workflow } from '../../types/workflow';
import UnpublishedChangesIndicator from '../Workflows/UnpublishedChangesIndicator';

interface WorkflowHeaderProps {
    currentWorkflow?: Workflow | null;
    onUpdateWorkflow: (workflowId: string, updatedWorkflow: Workflow) => void;
    onPublishWorkflow?: () => void;
    isPublishDisabled?: boolean;
    isPublishLoading?: boolean;
    isEnabled?: boolean;
    onToggleEnabled?: (enabled: boolean) => void;
}

const WorkflowHeader: React.FC<WorkflowHeaderProps> = ({ 
    currentWorkflow, 
    onUpdateWorkflow,
    onPublishWorkflow,
    isPublishDisabled = false,
    isPublishLoading = false,
    isEnabled = false,
    onToggleEnabled = () => {} ,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [editedName, setEditedName] = useState('');

    const handleStartEditing = () => {
        setEditedName(currentWorkflow?.name || '');
        setIsEditing(true);
    };

    const handleSaveWorkflowName = () => {
        if (currentWorkflow && currentWorkflow.id && editedName.trim()) {
            const updatedWorkflow = {
                ...currentWorkflow,
                name: editedName
            };
            
            onUpdateWorkflow(currentWorkflow.id, updatedWorkflow);
            setIsEditing(false);
        }
    };

    return (
        <div className="bg-white border-b border-borderGrey px-8 pt-6 pb-4">
            <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <div className="p-2 bg-backgroundBlue rounded-lg">
                        <Settings className="text-nanonetsBlue" />
                    </div>
                    <div className="flex-1">
                        {isEditing ? (
                            <div className="flex items-center gap-2">
                                <TextField
                                    size="small"
                                    value={editedName}
                                    onChange={(e) => setEditedName(e.target.value)}
                                    autoFocus
                                    onKeyPress={(e) => {
                                        if (e.key === 'Enter') {
                                            handleSaveWorkflowName();
                                        }
                                    }}
                                    onBlur={handleSaveWorkflowName}
                                    sx={{
                                        '& .MuiOutlinedInput-root': {
                                            fontSize: '1.5rem',
                                            fontWeight: 600,
                                            color: '#4B5563',
                                        }
                                    }}
                                />
                            </div>
                        ) : (
                            <div className="flex items-center gap-2">
                                <h1 className="text-2xl font-semibold text-greyishBlueMid">
                                    {currentWorkflow?.name || "New Workflow"}
                                </h1>
                                <IconButton 
                                    onClick={handleStartEditing}
                                    size="small"
                                    className="!p-1"
                                >
                                    <Edit fontSize="small" className="text-greyishBlue hover:text-nanonetsBlue transition-colors" />
                                </IconButton>
                                {currentWorkflow?.id && (
                                    <UnpublishedChangesIndicator workflowId={currentWorkflow.id} />
                                )}
                            </div>
                        )}
                        <p className="text-sm text-greyishBlue mt-1">
                            Configure and manage your workflow
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-3">
                    <div className="flex items-center gap-2.5 px-3 py-1.5 rounded-lg border border-borderGrey bg-backgroundGrey/50">
                        <span className={clsx(
                            "text-sm font-medium transition-colors",
                            isEnabled ? "text-[#18855E]" : "text-greyishBlue"
                        )}>
                            {isEnabled ? 'Enabled' : 'Disabled'}
                        </span>
                        <Switch
                            checked={isEnabled}
                            onChange={(e) => onToggleEnabled(e.target.checked)}
                            size="small"
                            sx={{
                                '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: '#18855E',
                                },
                                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: '#18855E',
                                },
                            }}
                        />
                    </div>
                    {onPublishWorkflow && (
                        <Button 
                            variant="contained" 
                            className={clsx(
                                "!normal-case !text-white !font-medium !px-4 !py-2 !rounded-lg !shadow-sm transition-all",
                                isPublishDisabled || isPublishLoading
                                    ? "!bg-greyishBlue !opacity-60 !cursor-not-allowed"
                                    : "!bg-nanonetsBlue hover:!bg-[#3B82F6] hover:!shadow-md"
                            )}
                            disabled={isPublishDisabled || isPublishLoading}
                            onClick={() => {
                                onPublishWorkflow();
                            }}
                            startIcon={isPublishLoading ? <CircularProgress size={16} color="inherit" /> : null}
                            sx={{
                                textTransform: 'none',
                                fontSize: '0.875rem',
                            }}
                        >
                            {isPublishLoading 
                                ? "Publishing..." 
                                : (isPublishDisabled ? "Complete workflow to publish" : "Publish")
                            }
                        </Button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default WorkflowHeader; 